import {
  BuildingsIcon,
  CaretRightIcon,
  CarProfileIcon,
  ClockIcon,
  TicketIcon,
} from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { useGetVisitorParkingSession } from '@/api/visitor-parking/get-visitor-parking-session'
import { Button } from '@/components/business/visitor-parking/button'
import { Card } from '@/components/business/visitor-parking/card'
import { DetailsCard } from '@/components/business/visitor-parking/details-card'
import { HelpSection } from '@/components/business/visitor-parking/help-section'
import { ParkingSessionError } from '@/components/business/visitor-parking/parking-session-error'
import { Icon } from '@/components/ui/icon'

import { ParkingTimeCounter } from '../parking-time-counter'
import { VISITOR_PARKING_TICKET_NUMBER_URL_KEY } from '../visitor-parking.constants'
import { useInvalidateVisitorParkingSessionOnSSE } from '../visitor-parking.hooks'
import { formatParkingEndTime } from '../visitor-parking.utils'
import { VisitorParkingFormLayout } from '../visitor-parking-form-layout'
import * as S from './parking-session.styled'

interface ParkingSessionProps {
  onExtendSession: () => void
}

export const ParkingSession = ({ onExtendSession }: ParkingSessionProps) => {
  const { t } = useTranslation(['visitor-parking'])
  const [searchParams] = useSearchParams()
  const ticketNumber = searchParams.get(VISITOR_PARKING_TICKET_NUMBER_URL_KEY) ?? ''
  const { data: parkingSession, isError, isLoading } = useGetVisitorParkingSession({ ticketNumber })
  const isFree = !parkingSession?.amount
  useInvalidateVisitorParkingSessionOnSSE({
    sessionId: parkingSession?.sessionId,
    ticketNumber,
  })
  return (
    <VisitorParkingFormLayout
      footer={
        <Button disabled={isError || isLoading} onClick={onExtendSession}>
          {t('visitor-parking:parkingSession.extend')} <Icon icon={CaretRightIcon} />
        </Button>
      }
    >
      <S.Title>{t('visitor-parking:parkingSession.title')}</S.Title>
      {!isError && <ParkingTimeCounter endDate={parkingSession?.endDate} loading={isLoading} />}
      {isError ? (
        <ParkingSessionError />
      ) : (
        <Card>
          <S.Heading>{t('visitor-parking:parkingSession.detailsHeading')}</S.Heading>
          <S.DetailsCardContainer>
            <DetailsCard
              icon={ClockIcon}
              isFree={isFree}
              // Note: endDate is saved when Stripe webhook is received. Backend needs to wait few seconds to properly set the endDate.
              // This component will show a loading state until the endDate is set.
              loading={isLoading || !parkingSession?.endDate}
              title={t('visitor-parking:parkingSession.endTime')}
              value={formatParkingEndTime(parkingSession?.endDate)}
              variant="compact"
            />
            <S.Separator />
            <DetailsCard
              icon={TicketIcon}
              loading={isLoading}
              title={t('visitor-parking:parkingSession.ticketNumber')}
              value={parkingSession?.ticketNumber ?? ''}
              variant="compact"
            />
            <S.Separator />
            <DetailsCard
              icon={BuildingsIcon}
              loading={isLoading}
              title={t('visitor-parking:parkingSession.propertyName')}
              value={parkingSession?.buildingName ?? ''}
              variant="compact"
            />
            <S.Separator />
            <DetailsCard
              icon={CarProfileIcon}
              loading={isLoading}
              title={t('visitor-parking:parkingSession.licensePlate')}
              value={parkingSession?.licensePlate ?? ''}
              variant="compact"
            />
          </S.DetailsCardContainer>
        </Card>
      )}
      <HelpSection />
    </VisitorParkingFormLayout>
  )
}
